/**
 * Comprehensive list of all available languages in the platform
 * This is used by language switchers to show all possible language options
 */

export interface Language {
  code: string;
  label: string;
  flag: string;
  nativeName?: string;
}

/**
 * Languages that are actually supported in the platform (have translation files)
 * This should match the languages loaded in i18n.ts
 */
export const SUPPORTED_PLATFORM_LANGUAGES: Language[] = [
  { code: "en", label: "English", flag: "🇺🇸", nativeName: "English" },
  { code: "de", label: "German", flag: "🇩🇪", nativeName: "Deutsch" },
  { code: "ar", label: "Arabic", flag: "🇸🇦", nativeName: "العربية" },
];

/**
 * Comprehensive list of all possible languages (for future expansion)
 * This includes languages that may be added in the future but don't have translation files yet
 */
export const ALL_PLATFORM_LANGUAGES: Language[] = [
  { code: "en", label: "English", flag: "🇺🇸", nativeName: "English" },
  { code: "de", label: "German", flag: "🇩🇪", nativeName: "Deutsch" },
  { code: "ar", label: "Arabic", flag: "🇸🇦", nativeName: "العربية" },
  { code: "fr", label: "French", flag: "🇫🇷", nativeName: "Français" },
  { code: "es", label: "Spanish", flag: "🇪🇸", nativeName: "Español" },
  { code: "it", label: "Italian", flag: "🇮🇹", nativeName: "Italiano" },
  { code: "pt", label: "Portuguese", flag: "🇵🇹", nativeName: "Português" },
  { code: "nl", label: "Dutch", flag: "🇳🇱", nativeName: "Nederlands" },
  { code: "ru", label: "Russian", flag: "🇷🇺", nativeName: "Русский" },
  { code: "zh", label: "Chinese", flag: "🇨🇳", nativeName: "中文" },
  { code: "ja", label: "Japanese", flag: "🇯🇵", nativeName: "日本語" },
  { code: "ko", label: "Korean", flag: "🇰🇷", nativeName: "한국어" },
  { code: "tr", label: "Turkish", flag: "🇹🇷", nativeName: "Türkçe" },
  { code: "pl", label: "Polish", flag: "🇵🇱", nativeName: "Polski" },
  { code: "sv", label: "Swedish", flag: "🇸🇪", nativeName: "Svenska" },
  { code: "no", label: "Norwegian", flag: "🇳🇴", nativeName: "Norsk" },
  { code: "da", label: "Danish", flag: "🇩🇰", nativeName: "Dansk" },
  { code: "fi", label: "Finnish", flag: "🇫🇮", nativeName: "Suomi" },
  { code: "cs", label: "Czech", flag: "🇨🇿", nativeName: "Čeština" },
  { code: "hu", label: "Hungarian", flag: "🇭🇺", nativeName: "Magyar" },
  { code: "ro", label: "Romanian", flag: "🇷🇴", nativeName: "Română" },
  { code: "el", label: "Greek", flag: "🇬🇷", nativeName: "Ελληνικά" },
  { code: "he", label: "Hebrew", flag: "🇮🇱", nativeName: "עברית" },
  { code: "hi", label: "Hindi", flag: "🇮🇳", nativeName: "हिन्दी" },
  { code: "th", label: "Thai", flag: "🇹🇭", nativeName: "ไทย" },
  { code: "vi", label: "Vietnamese", flag: "🇻🇳", nativeName: "Tiếng Việt" },
  { code: "id", label: "Indonesian", flag: "🇮🇩", nativeName: "Bahasa Indonesia" },
  { code: "ms", label: "Malay", flag: "🇲🇾", nativeName: "Bahasa Melayu" },
  { code: "uk", label: "Ukrainian", flag: "🇺🇦", nativeName: "Українська" },
  { code: "bg", label: "Bulgarian", flag: "🇧🇬", nativeName: "Български" },
  { code: "hr", label: "Croatian", flag: "🇭🇷", nativeName: "Hrvatski" },
  { code: "sk", label: "Slovak", flag: "🇸🇰", nativeName: "Slovenčina" },
  { code: "sl", label: "Slovenian", flag: "🇸🇮", nativeName: "Slovenščina" },
  { code: "sr", label: "Serbian", flag: "🇷🇸", nativeName: "Српски" },
  { code: "et", label: "Estonian", flag: "🇪🇪", nativeName: "Eesti" },
  { code: "lv", label: "Latvian", flag: "🇱🇻", nativeName: "Latviešu" },
  { code: "lt", label: "Lithuanian", flag: "🇱🇹", nativeName: "Lietuvių" },
];

/**
 * Get language by code
 */
export function getLanguageByCode(code: string): Language | undefined {
  return ALL_PLATFORM_LANGUAGES.find((lang) => lang.code === code);
}

/**
 * Get all language codes
 */
export function getAllLanguageCodes(): string[] {
  return ALL_PLATFORM_LANGUAGES.map((lang) => lang.code);
}

/**
 * Check if a language code is supported
 */
export function isLanguageSupported(code: string): boolean {
  return ALL_PLATFORM_LANGUAGES.some((lang) => lang.code === code);
}

